<template>
  <div>
    <Header :header="newBannerGroup ? $t('NEW_BANNER_GROUP') : bannerGroup.name" />

    <RenderSpinner v-if="loading" />
    <b-container class="mt-5">
      <h2 class="card-title">
        {{ newBannerGroup ? $t('CREATE_BANNER_GROUP') : $t('EDIT_BANNER_GROUP') }}
      </h2>

      <!-- Banner Group Form -->
      <b-card class="mt-4">
        <b-form>
          <b-form-group :label="$t('BANNER_GROUP_NAME')" label-for="banner-group-name">
            <b-form-input
              id="banner-group-name"
              v-model="bannerGroup.name"
              required
              :placeholder="$t('ENTER_BANNER_GROUP_NAME')"
            />
          </b-form-group>

          <b-form-group :label="$t('BANNER_GROUP_DESCRIPTION')" label-for="banner-group-description">
            <b-form-textarea
              id="banner-group-description"
              v-model="bannerGroup.description"
              class="textarea-secondary"
              rows="3"
              :placeholder="$t('ENTER_DESCRIPTION_OPTIONAL')"
            />
          </b-form-group>
        </b-form>
      </b-card>

      <!-- Banners List -->
      <b-card class="mt-4 banner-list-card" v-if="!newBannerGroup">
        <b-btn
          variant="primary"
          size="sm"
          class="mb-3"
          @click="openBannerModal()"
        >
          {{ $t('ADD_BANNER') }}
        </b-btn>

        <div v-if="banners.length > 0" class="banner-cards">
          <b-card
            v-for="banner in banners"
            :key="banner.id"
            class="banner-card"
            no-body
          >
            <div class="banner-card-wrapper">
              <div class="banner-content-wrapper">
                <div
                  class="text-truncate cursor-pointer banner-html"
                  @click="e => e.target.classList.toggle('text-truncate')"
                  role="button"
                  v-html="banner.htmlContent"
                ></div>

                <!-- Overlay Info Badge - Top Left -->
                <div class="banner-order-overlay">
                  <!-- For Carousel: Show order number and duration -->
                  <template v-if="banner.bannerType === 'LIMIT_BANNER_CAROUSEL'">
                    <span class="order-badge-overlay">#{{ banner.orderIndex }}</span>
                    <span class="duration-badge-overlay">{{ banner.displayDurationSeconds }}s</span>
                  </template>
                  <!-- For Success: Show checkmark icon only -->
                  <span v-else-if="banner.bannerType === 'LIMIT_RESULT_SUCCESS'" class="type-icon-badge success-icon">
                    ✓
                  </span>
                  <!-- For Manual: Show hand/info icon only -->
                  <span v-else-if="banner.bannerType === 'LIMIT_RESULT_MANUAL'" class="type-icon-badge manual-icon">
                    ℹ
                  </span>
                </div>

                <!-- Overlay Action Buttons - Top Right -->
                <div class="banner-actions-overlay">
                  <span v-b-tooltip.hover :title="$t('EDIT')">
                    <a @click="openBannerModal(banner)" class="action-btn">
                      <img alt="edit" src="../../assets/edit-icon.svg" />
                    </a>
                  </span>
                  <span v-b-tooltip.hover :title="$t('DELETE')">
                    <a @click="openDeleteBannerModal(banner.id)" class="action-btn">
                      <img alt="delete" src="../../assets/delete-icon.svg" />
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </b-card>
        </div>
        <p v-else class="text-muted">{{ $t('NO_BANNERS_YET') }}</p>
      </b-card>

      <!-- Action Buttons -->
      <div class="mb-5 mt-4">
        <b-row align-h="center">
          <b-col md="4">
            <b-btn
              variant="secondary"
              size="lg"
              block
              :to="{ name: 'ManageBannerGroups' }"
            >
              {{ $t('BACK') }}
            </b-btn>
          </b-col>
          <b-col md="4">
            <b-btn variant="primary" size="lg" block @click="saveBannerGroup">
              {{ $t('SAVE') }}
            </b-btn>
          </b-col>
        </b-row>
      </div>
    </b-container>

    <!-- Banner Modal -->
    <b-modal
      v-model="showBannerModal"
      :title="editingBanner.id ? $t('EDIT_BANNER') : $t('ADD_BANNER')"
      size="lg"
      @ok="saveBanner"
      @cancel="closeBannerModal"
    >
      <b-form>
        <b-form-group :label="$t('BANNER_TYPE')" label-for="banner-type">
          <b-form-select
            id="banner-type"
            v-model="editingBanner.bannerType"
            :options="bannerTypeOptions"
          >
            <template #first>
              <b-form-select-option :value="null">-- Select Banner Type --</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group :label="$t('BANNER_DISPLAY_DURATION')" label-for="banner-duration">
          <b-form-input
            id="banner-duration"
            v-model.number="editingBanner.displayDurationSeconds"
            type="number"
            required
            min="1"
            :placeholder="$t('BANNER_DURATION_PLACEHOLDER')"
          />
        </b-form-group>

        <b-form-group
          v-if="editingBanner.bannerType === 'LIMIT_BANNER_CAROUSEL'"
          :label="$t('BANNER_ORDER_INDEX')"
          label-for="banner-order"
        >
          <b-form-input
            id="banner-order"
            v-model.number="editingBanner.orderIndex"
            type="number"
            required
            min="0"
            :placeholder="$t('BANNER_ORDER_PLACEHOLDER')"
          />
        </b-form-group>

        <b-form-group :label="$t('BANNER_ON_CLICK_URL')" label-for="on-click-url">
          <b-form-input
            id="on-click-url"
            v-model="editingBanner.onClickUrl"
            type="text"
            placeholder="Enter URL (optional)"
          />
        </b-form-group>

        <b-form-group :label="$t('BANNER_HTML_CONTENT')" label-for="banner-content">
          <b-form-textarea
            id="banner-content"
            v-model="editingBanner.htmlContent"
            class="textarea-secondary"
            rows="8"
            required
            :placeholder="$t('ENTER_HTML_CONTENT')"
          />
        </b-form-group>

        <b-form-group :label="$t('BANNER_PREVIEW')">
          <div
            class="border p-3"
            style="min-height: 100px; background-color: #f8f9fa"
            v-html="editingBanner.htmlContent"
          ></div>
        </b-form-group>
      </b-form>
    </b-modal>

    <!-- Delete Banner Modal -->
    <RenderDeleteModal
      :show="showDeleteBannerModal"
      @ok="closeDeleteBannerModal(true)"
      @cancel="closeDeleteBannerModal()"
      :label="$t('ARE_YOU_SURE_DELETE_BANNER')"
    />
  </div>
</template>

<script>
import { Header, RenderSpinner, RenderDeleteModal } from '@/components/common';
import { mapState } from 'vuex';

export default {
  name: 'EditBannerGroup',
  components: {
    Header,
    RenderSpinner,
    RenderDeleteModal,
  },
  props: {
    newBannerGroup: {
      type: Boolean,
      default: true,
    },
  },
  created() {
    if (!this.newBannerGroup) {
      this.fetchBannerGroup();
    }
  },
  data() {
    return {
      bannerGroup: {
        id: null,
        name: '',
        description: '',
      },
      banners: [],
      showBannerModal: false,
      editingBanner: {
        id: null,
        orderIndex: 0,
        displayDurationSeconds: 5,
        htmlContent: '',
        bannerType: null,
        onClickUrl: '',
        bannerGroup: null,
      },
      bannerTypeOptions: [
        { value: 'LIMIT_BANNER_CAROUSEL', text: 'Limit Banner Carousel' },
        { value: 'LIMIT_RESULT_SUCCESS', text: 'Limit Result - Success' },
        { value: 'LIMIT_RESULT_MANUAL', text: 'Limit Result - Manual' },
      ],
      showDeleteBannerModal: false,
      selectedBannerId: null,
    };
  },
  methods: {
    fetchBannerGroup() {
      const bannerGroupId = this.$route.params.id;
      this.$store
        .dispatch('getBannerGroup', bannerGroupId)
        .then(response => {
          this.bannerGroup = response.data;
          this.fetchBanners();
        })
        .catch(error => {
          console.log(error);
          this.$store.dispatch('alert/ALERT', {
            message: this.$t('BANNER_GROUP_LOAD_FAILED'),
          }, { root: true });
        });
    },
    fetchBanners() {
      if (!this.bannerGroup.id) return;

      this.$store
        .dispatch('getBannersByGroupId', this.bannerGroup.id)
        .then(response => {
          this.banners = response.data || [];
        })
        .catch(error => {
          console.log(error);
        });
    },
    saveBannerGroup() {
      if (!this.bannerGroup.name) {
        this.$store.dispatch('alert/ALERT', {
          message: this.$t('BANNER_GROUP_NAME_REQUIRED'),
        }, { root: true });
        return;
      }

      this.$store
        .dispatch('saveBannerGroup', this.bannerGroup)
        .then(response => {
          this.$store.dispatch('alert/ALERT', {
            message: this.$t('BANNER_GROUP_SAVED'),
            type: 'success',
          }, { root: true });

          if (this.newBannerGroup) {
            this.bannerGroup = response.data; // Update local state with saved data
            this.newBannerGroup = false; // Mark as no longer new
            this.$router.push({
              name: 'EditBannerGroup',
              params: { id: response.data.id },
            }).then(() => {
              this.fetchBannerGroup(); // Fetch fresh data after navigation
            });
          } else {
            this.fetchBannerGroup();
          }
        })
        .catch(error => {
          console.log(error);
          this.$store.dispatch('alert/ALERT', {
            message: this.$t('BANNER_GROUP_SAVE_FAILED'),
          }, { root: true });
        });
    },
    openBannerModal(banner = null) {
      if (banner) {
        this.editingBanner = { ...banner };
      } else {
        this.editingBanner = {
          id: null,
          orderIndex: this.banners.length,
          displayDurationSeconds: 5,
          htmlContent: '',
          bannerType: null,
          onClickUrl: '',
          bannerGroup: { id: this.bannerGroup.id },
        };
      }
      this.showBannerModal = true;
    },
    closeBannerModal() {
      this.showBannerModal = false;
      this.editingBanner = {
        id: null,
        orderIndex: 0,
        displayDurationSeconds: 5,
        htmlContent: '',
        bannerType: null,
        onClickUrl: '',
        bannerGroup: null,
      };
    },
    saveBanner(bvModalEvt) {
      bvModalEvt.preventDefault();

      if (!this.editingBanner.htmlContent) {
        this.$store.dispatch('alert/ALERT', {
          message: this.$t('BANNER_CONTENT_REQUIRED'),
        }, { root: true });
        return;
      }

      if (!this.editingBanner.bannerGroup) {
        this.editingBanner.bannerGroup = { id: this.bannerGroup.id };
      }

      this.$store
        .dispatch('saveBanner', this.editingBanner)
        .then(() => {
          this.$store.dispatch('alert/ALERT', {
            message: this.$t('BANNER_SAVED'),
            type: 'success',
          }, { root: true });
          this.closeBannerModal();
          this.fetchBanners();
        })
        .catch(error => {
          console.log(error);
          this.$store.dispatch('alert/ALERT', {
            message: this.$t('BANNER_SAVE_FAILED'),
          }, { root: true });
        });
    },
    openDeleteBannerModal(bannerId) {
      this.selectedBannerId = bannerId;
      this.showDeleteBannerModal = true;
    },
    closeDeleteBannerModal(doDelete) {
      this.showDeleteBannerModal = false;
      if (doDelete && this.selectedBannerId) {
        this.$store
          .dispatch('deleteBanner', this.selectedBannerId)
          .then(() => {
            this.$store.dispatch('alert/ALERT', {
              message: this.$t('BANNER_DELETED'),
              type: 'success',
            }, { root: true });
            this.selectedBannerId = null;
            this.fetchBanners();
          });
      }
    },
  },
  computed: {
    ...mapState(['loading']),
  },
};
</script>

<style scoped>
.banner-cards {
  margin-top: 1rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  align-items: start;
}

.banner-card {
  border: none !important;
  background: transparent !important;
  transition: box-shadow 0.2s, transform 0.2s;
  overflow: visible;
  position: relative;
  margin: 0 !important;
  padding: 0 !important;
}

.banner-card:hover {
  box-shadow: none;
  transform: translateY(-2px);
}

.banner-card .card-body {
  padding: 0 !important;
}

.banner-card-wrapper {
  display: block;
  width: 100%;
}

.banner-content-wrapper {
  position: relative;
  display: inline-block;
  width: auto;
  vertical-align: top;
}

.banner-html {
  padding: 0;
  margin: 0;
  width: auto;
  display: inline-block;
  vertical-align: top;
}

/* Overlay Order Badge - Top Left */
.banner-order-overlay {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}

.banner-content-wrapper:hover .banner-order-overlay {
  opacity: 1;
}

.order-badge-overlay {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 12px;
  background-color: rgba(0, 123, 255, 0.95);
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 18px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.type-icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 12px;
  color: white;
  font-weight: 700;
  font-size: 1.2rem;
  border-radius: 18px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.type-icon-badge.success-icon {
  background-color: rgba(40, 167, 69, 0.95);
}

.type-icon-badge.manual-icon {
  background-color: rgba(255, 193, 7, 0.95);
}

.duration-badge-overlay {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 12px;
  background-color: rgba(108, 117, 125, 0.95);
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 18px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

/* Overlay Action Buttons - Top Right */
.banner-actions-overlay {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}

.banner-content-wrapper:hover .banner-actions-overlay {
  opacity: 1;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
  background-color: rgba(255, 255, 255, 1);
}

.action-btn img {
  width: 18px;
  height: 18px;
}

.cursor-pointer {
  cursor: pointer;
}

/* Remove styling from banner list container card */
.banner-list-card {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.banner-list-card .card-body {
  padding: 0 !important;
}
</style>
